import React, { useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import styled from "styled-components";
import { Cancel, ChevronLeft, ChevronRight } from "../../../styled/AllImages";
import { useTranslation } from "react-i18next";

// Media query breakpoint
const MOBILE_BREAKPOINT = "769px";

const GalleryModal = ({ show, onHide, type, items = [] }) => {
  const {
    i18n: { language },
  } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const thumbnailGridRef = useRef(null);
  const thumbnailWrapperRef = useRef(null);
  const videoRef = useRef(null);

  const currentItem = items[activeIndex];
  const isVideo = type?.includes("video");

  // Calculate scroll positions
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  // Check if device is mobile
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);

    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  // Update scroll button states
  const updateScrollButtons = () => {
    if (!thumbnailGridRef.current || !thumbnailWrapperRef.current) return;

    const { scrollLeft, scrollWidth, clientWidth } = thumbnailGridRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
  };

  // Handle thumbnail click
  const handleThumbnailClick = (index) => {
    setActiveIndex(index);
    setIsVideoPlaying(false);
    scrollToThumbnail(index);
  };

  // Scroll to specific thumbnail
  const scrollToThumbnail = (index) => {
    if (!thumbnailGridRef.current) return;

    const grid = thumbnailGridRef.current;
    const thumbnails = grid.children;
    if (!thumbnails || index >= thumbnails.length) return;

    const thumbnail = thumbnails[index];
    const scrollPosition =
      thumbnail.offsetLeft - (grid.clientWidth - thumbnail.offsetWidth) / 2;

    grid.scrollTo({
      left: scrollPosition,
      behavior: "smooth",
    });
  };

  // Scroll thumbnails in direction
  // const scrollThumbnails = (direction) => {
  //   if (direction === "left") {
  //     if (activeIndex > 0) {
  //       setActiveIndex((prev) => prev - 1);
  //       setIsVideoPlaying(false);
  //     }
  //   } else {
  //     if (activeIndex < items.length - 1) {
  //       setActiveIndex((prev) => prev + 1);
  //       setIsVideoPlaying(false);
  //     }
  //   }
  // };
  const scrollThumbnails = (direction) => {
    if (direction === "left") {
      if (activeIndex > 0) {
        const newIndex = activeIndex - 1;
        setActiveIndex(newIndex);
        setIsVideoPlaying(false);
        scrollToThumbnail(newIndex); // Center on arrow click
      }
    } else {
      if (activeIndex < items.length - 1) {
        const newIndex = activeIndex + 1;
        setActiveIndex(newIndex);
        setIsVideoPlaying(false);
        scrollToThumbnail(newIndex); // Center on arrow click
      }
    }
  };

  const handlePlayVideo = () => {
    setIsVideoPlaying(true);
    if (videoRef.current) {
      videoRef.current.play().catch((error) => {
        console.error("Video play failed:", error);
      });
    }
  };

  // Reset when modal opens
  useEffect(() => {
    if (show) {
      setActiveIndex(0);
      setIsVideoPlaying(false);

      // Wait for the DOM to update before scrolling
      const timer = setTimeout(() => {
        scrollToThumbnail(0);
        updateScrollButtons();
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [show]);

  // Add scroll event listener
  useEffect(() => {
    const grid = thumbnailGridRef.current;
    if (!grid) return;

    grid.addEventListener("scroll", updateScrollButtons);
    return () => grid.removeEventListener("scroll", updateScrollButtons);
  }, []);

  // Update scroll buttons when items change
  useEffect(() => {
    updateScrollButtons();
  }, [items]);

  // Handle keyboard navigation on mobile
  useEffect(() => {
    if (!show || !isMobile) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onHide();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [show, isMobile, onHide]);

  if (!currentItem) return null;

  return (
    <StyledModal
      show={show}
      onHide={onHide}
      centered={!isMobile}
      size="xl"
      fullscreen={isMobile ? true : false}
    >
      <Modal.Body>
        <GalleryContainer>
          <CloseButton onClick={onHide} aria-label="Close gallery">
            <Cancel width="15px" />
          </CloseButton>

          <MainImageContainer>
            <CounterText>
              {activeIndex + 1} / {items.length}
            </CounterText>
            {isMobile && items?.length > 1 && (
              <>
                <ArrowButton
                  onClick={() => scrollThumbnails("left")}
                  aria-label="Previous"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "10px",
                    transform: "translateY(-50%)",
                  }}
                >
                  <ChevronLeft
                    style={
                      {
                        // transform: language === "ar" ? "scale(-1)" : "scale(1)",
                      }
                    }
                  />
                </ArrowButton>

                <ArrowButton
                  onClick={() => scrollThumbnails("right")}
                  aria-label="Next"
                  style={{
                    position: "absolute",
                    top: "50%",
                    right: "10px",
                    transform: "translateY(-50%)",
                  }}
                >
                  <ChevronRight
                    style={
                      {
                        // transform: language === "ar" ? "scale(-1)" : "scale(1)",
                      }
                    }
                  />
                </ArrowButton>
              </>
            )}

            {isVideo ? (
              <>
                {isVideoPlaying ? (
                  <MainVideo
                    ref={videoRef}
                    src={currentItem}
                    controls
                    autoPlay
                    onEnded={() => setIsVideoPlaying(false)}
                  />
                ) : (
                  <>
                    <video
                      src={currentItem}
                      muted
                      playsInline
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                      preload="metadata"
                    />
                    <PlayButton onClick={handlePlayVideo} />
                  </>
                )}
              </>
            ) : (
              <MainImage src={currentItem} alt={`Image ${activeIndex + 1}`} />
            )}
          </MainImageContainer>

          <ThumbnailContainer>
            {!isMobile && (
              <ArrowButton
                onClick={() => scrollThumbnails("left")}
                // disabled={!canScrollLeft}
                aria-label="Scroll thumbnails left"
              >
                <ChevronLeft
                  style={{
                    transform: language === "ar" ? "scale(-1)" : "scale(1)",
                  }}
                />
              </ArrowButton>
            )}

            <ThumbnailWrapper ref={thumbnailWrapperRef}>
              <ThumbnailGrid ref={thumbnailGridRef}>
                {items.map((item, index) => (
                  <Thumbnail
                    key={index}
                    $active={index === activeIndex}
                    onClick={() => handleThumbnailClick(index)}
                  >
                    {isVideo ? (
                      <>
                        <video
                          src={item}
                          muted
                          playsInline
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                          preload="metadata"
                        />
                        <VideoIndicator />
                      </>
                    ) : (
                      <img src={item} alt={`Thumbnail ${index + 1}`} />
                    )}
                  </Thumbnail>
                ))}
              </ThumbnailGrid>
            </ThumbnailWrapper>
            {!isMobile && (
              <ArrowButton
                onClick={() => scrollThumbnails("right")}
                // disabled={!canScrollRight}
                aria-label="Scroll thumbnails right"
              >
                <ChevronRight
                  style={{
                    transform: language === "ar" ? "scale(-1)" : "scale(1)",
                  }}
                />
              </ArrowButton>
            )}
          </ThumbnailContainer>
        </GalleryContainer>
      </Modal.Body>
    </StyledModal>
  );
};

export default GalleryModal;

// Styled Components
const StyledModal = styled(Modal)`
  z-index: 9999999;
  .modal-dialog {
    margin: 1rem auto 0 auto;
    max-width: 900px;

    @media (max-width: ${MOBILE_BREAKPOINT}) {
      margin: 0;
      max-width: 100%;
      height: 100vh;
    }
  }

  .modal-content {
    background: var(--white);
    border: none;
    border-radius: 16px;
    overflow: hidden;

    @media (max-width: ${MOBILE_BREAKPOINT}) {
      border-radius: 0;
      height: 100vh;
    }
  }

  .modal-body {
    padding: 0;
  }
`;

const GalleryContainer = styled.div`
  padding: 20px;
  border-radius: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    padding: 16px;
    height: 100vh;
    display: flex;
    flex-direction: column;
    border-radius: 0;
  }
`;

const MainImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 65vh;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 16px;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex: 1;
    height: auto;
    border-radius: 18px;
    margin-bottom: 12px;
  }
`;

const CounterText = styled.div`
  position: absolute;
  top: 12px;
  left: 16px;
  background: rgba(0, 0, 0, 0.5);
  color: white;
  font-size: 14px;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 500;
  z-index: 2;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    font-size: 12px;
    padding: 3px 8px;
  }
`;

const MainImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const MainVideo = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 48px;
    height: 48px;
  }

  &:hover {
    background: white;
    transform: translate(-50%, -50%) scale(1.05);
  }

  &::after {
    content: "";
    width: 0;
    height: 0;
    border-left: 18px solid #333;
    border-top: 12px solid transparent;
    border-bottom: 12px solid transparent;
    margin-left: 4px;

    @media (max-width: ${MOBILE_BREAKPOINT}) {
      border-left: 14px solid #333;
      border-top: 9px solid transparent;
      border-bottom: 9px solid transparent;
      margin-left: 3px;
    }
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;

  /* @media (max-width: ${MOBILE_BREAKPOINT}) {
    flex-shrink: 0;
  } */
`;

const ThumbnailWrapper = styled.div`
  flex: 1;
  overflow: hidden;
`;

const ThumbnailGrid = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding-bottom: 10px;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x proximity;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    gap: 6px;
    padding-bottom: 8px;
  }

  &::-webkit-scrollbar {
    height: 4px;

    @media (max-width: ${MOBILE_BREAKPOINT}) {
      height: 2px;
    }
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const Thumbnail = styled.div`
  position: relative;
  width: 220px;
  height: 130px;
  flex-shrink: 0;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  /* border: 2px solid ${(props) =>
    props.$active ? "#ffffff" : "transparent"}; */

  border: 2px solid
    ${(props) => (props.$active ? "rgba(255,255,255,0.9)" : "transparent")};
  box-shadow: ${(props) =>
    props.$active ? "0 0 0 2px rgba(255, 255, 255, 0.6)" : "none"};
  transition: all 0.3s ease;
  scroll-snap-align: center;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 121px;
    height: 120px;
    border-radius: 18px;
    border-width: 1.5px;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.6);
  }

  img,
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const VideoIndicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 24px;
  height: 24px;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 16px;
    height: 16px;
  }

  &::after {
    content: "";
    width: 0;
    height: 0;
    border-left: 8px solid #333;
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    margin-left: 2px;

    @media (max-width: ${MOBILE_BREAKPOINT}) {
      border-left: 5px solid #333;
      border-top: 3px solid transparent;
      border-bottom: 3px solid transparent;
      margin-left: 1px;
    }
  }
`;

const ArrowButton = styled.button`
  background: rgba(255, 255, 255, 0.8);
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 14px;
  font-weight: bold;
  color: #333;
  margin: 0 8px;
  z-index: 1;

  @media (max-width: ${MOBILE_BREAKPOINT}) {
    width: 24px;
    height: 24px;
    margin: 0 4px;
    font-size: 12px;
  }

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: scale(1.1);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;

    &:hover {
      transform: none;
      background: rgba(255, 255, 255, 0.8);
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 26px;
  right: 26px;
  background: rgba(0, 0, 0, 0.4);
  border: none;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.3s ease;

  svg {
    [fill] {
      fill: var(--white);
    }
    [stroke] {
      stroke: var(--white);
    }
  }

  &:hover {
    background: rgba(0, 0, 0, 0.6);
    transform: scale(1.1);
  }
`;
